import { useEffect, useState, type ReactNode } from 'react';
import { Icon, ICON_SIZE, type IconName } from '../chrome/icons';
import { useWork } from '../work';
import { AddonsPanel } from './AddonsPanel';
import { BrandMark, BrandPanel } from './brand';
import { railMark } from './brandModel';
import { PanelHeader } from './parts';
import { Body, MyStuffPanel, ShapesPanel, TextPanel, type LibraryActions } from './kinds';
import { GraphicsPanel, TemplatesPanel } from './catalog';
import { ElementsPanel } from './ElementsPanel';

/**
 * Studio rail keeps the categories beside a roomy browser for real Flobi, Drive,
 * shape, type, brand and add-on content. Upload and Layers stay at its foot.
 */

interface Kind {
  readonly id: string;
  readonly name: string;
  readonly icon: IconName;
  /** The panel draws its own top: the Brand tab's is the brand itself. */
  readonly ownsHeader?: boolean;
  /**
   * Not on the rail. Shapes and Text are the dock's (the same shapes and type modes twice
   * was one of the audit's duplicates, 15 Oct); Add-ons are a Pro thing, under More. The
   * panels stay, so a hint or More can still open them.
   */
  readonly hidden?: boolean;
  readonly panel: (actions: LibraryActions, onClose: () => void) => ReactNode;
}

const KINDS: readonly Kind[] = [
  { id: 'generate', name: 'Generate', icon: 'generate-sparkles', panel: () => null },
  { id: 'elements', name: 'Elements', icon: 'elements', panel: (actions) => <ElementsPanel projectId={actions.projectId} onUse={actions.useElement} onNotice={actions.notice} /> },
  { id: 'templates', name: 'Templates', icon: 'templates', panel: (actions) => <TemplatesPanel actions={actions} /> },
  { id: 'graphics', name: 'Graphics', icon: 'sparkles', panel: (actions) => <GraphicsPanel actions={actions} /> },
  { id: 'mystuff', name: 'My Stuff', icon: 'folder', ownsHeader: true, panel: (actions, onClose) => <MyStuffPanel actions={actions} onClose={onClose} /> },
  { id: 'shapes', name: 'Shapes', icon: 'shape', hidden: true, panel: (actions) => <ShapesPanel actions={actions} /> },
  { id: 'text', name: 'Text', icon: 'text', hidden: true, panel: (actions) => <TextPanel actions={actions} /> },
  { id: 'brand', name: 'Brand', icon: 'brands', ownsHeader: true, panel: (actions, onClose) => <BrandPanel actions={actions} onClose={onClose} /> },
  { id: 'addons', name: 'Add-ons', icon: 'addons', hidden: true, panel: () => <Body><AddonsPanel /></Body> },
];

const COARSE = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;
/** The category rail and the content browser beside it (72 + 360 under a finger); also used by the canvas inset. */
export const LIBRARY_RAIL_WIDTH = COARSE ? 72 : 57;
export const ASSETS_WIDTH = COARSE ? 432 : 338;

/** Kinds that became another: Pictures and Uploads are My Stuff now. */
const BECAME: Record<string, string> = { pictures: 'mystuff', uploads: 'mystuff' };

/** Which kind Assets shows, remembered for the next time it is opened. */
export function useLibraryPlace(): [string, (place: string) => void] {
  const [chosen, setChosen] = useState(() => {
    try {
      const kept = localStorage.getItem('artwork.library') ?? 'mystuff';
      return BECAME[kept] ?? kept;
    } catch {
      return 'mystuff';
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('artwork.library', chosen);
    } catch {
      // Private windows keep it for the visit.
    }
  }, [chosen]);
  return [chosen, setChosen];
}

/** The same navigation stays beside every Assets category and the Layers view. */
export function LibraryRail({ chosen, onChoose, onLayers, onUpload }: { chosen: string; onChoose: (place: string) => void; onLayers: () => void; onUpload: () => void }) {
  const { context, kit } = useWork();
  // With a brand in force the rail wears its mark: the tab is that brand, not brands in general.
  const mark = kit && context?.brand && kit.id === context.brand.id ? railMark(kit) : null;
  // A file made in a brand is offered the brand's own things first.
  const shown = KINDS.filter((one) => !one.hidden);
  const kinds = context?.brand ? [...shown.filter((one) => one.id === 'brand'), ...shown.filter((one) => one.id !== 'brand')] : shown;
  return <div className="library-rail">
    <nav aria-label="Asset categories" className="library-categories ui-scroll" data-tour="artwork.rail">
      {kinds.map((one) => {
        const on = one.id === chosen;
        return (
          <button
            key={one.id}
            type="button"
            aria-pressed={on}
            data-tour={`artwork.rail.${one.id}`}
            onClick={() => onChoose(one.id)}
            className="library-rail-button"
          >
            {one.id === 'brand' && mark ? <BrandMark logo={mark} size={ICON_SIZE.navigation} /> : <Icon name={one.icon} size={ICON_SIZE.navigation} />}
            <span>{one.name}</span>
          </button>
        );
      })}
    </nav>
    <div className="library-rail-actions">
      <button type="button" onClick={onUpload} className="library-rail-button"><Icon name="upload" size={ICON_SIZE.navigation} /><span>Upload</span></button>
      <button type="button" aria-pressed={chosen === 'layers'} onClick={onLayers} className="library-rail-button"><Icon name="nav-layers" size={ICON_SIZE.navigation} /><span>Layers</span></button>
    </div>
  </div>;
}

export function Library({ open, onClose, onLayers, chosen, onChoose, actions, panelOnly = false, generationHost, override }: { open: boolean; onClose: () => void; onLayers: () => void; chosen: string; onChoose: (place: string) => void; actions: LibraryActions; panelOnly?: boolean; generationHost?: (host: HTMLDivElement | null) => void;
  /** Something standing in the panel's place for a while: a picture's Edit with AI, in the Generate slot. */
  override?: { title: string; node: ReactNode; back?: () => void; backLabel?: string } | null;
}) {
  if (!open) return null;
  const kind = KINDS.find((one) => one.id === chosen) ?? KINDS.find(one => one.id === 'templates')!;
  if (override) {
    return (
      <div onKeyDown={(event) => { if (event.key === 'Escape' && !event.defaultPrevented && event.currentTarget.contains(event.target as Node)) onClose(); }}
        className={`workspace-navigation workspace-library ${panelOnly ? 'workspace-navigation-content' : ''} glass ui-pop pointer-events-auto absolute left-[8px] z-20 flex overflow-hidden`}
        style={{ width: panelOnly ? ASSETS_WIDTH - LIBRARY_RAIL_WIDTH : ASSETS_WIDTH }}>
        {!panelOnly && <LibraryRail chosen={kind.id} onChoose={onChoose} onLayers={onLayers} onUpload={actions.upload} />}
        <aside aria-label={override.title} className="library-browser flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <PanelHeader title={override.title} onClose={onClose} back={override.back} backLabel={override.backLabel} />
          <div className="ui-scroll flex min-h-0 flex-1 flex-col overflow-auto">{override.node}</div>
        </aside>
      </div>
    );
  }
  return (
    <div
      onKeyDown={(event) => {
        // Only its own keys: a popup portalled from inside (the element popup) bubbles through here too.
        if (event.key === 'Escape' && !event.defaultPrevented && event.currentTarget.contains(event.target as Node)) onClose();
      }}
      className={`workspace-navigation workspace-library ${panelOnly ? 'workspace-navigation-content' : ''} glass ui-pop pointer-events-auto absolute left-[8px] z-20 flex overflow-hidden`}
      style={{ width: panelOnly ? ASSETS_WIDTH - LIBRARY_RAIL_WIDTH : ASSETS_WIDTH }}
    >
      {!panelOnly && <LibraryRail chosen={kind.id} onChoose={onChoose} onLayers={onLayers} onUpload={actions.upload} />}
      <aside aria-label={kind.name} className="library-browser flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {kind.ownsHeader ? (
          kind.panel(actions, onClose)
        ) : (
          <>
            <PanelHeader title={kind.name} onClose={onClose} />
            {kind.id === 'generate' ? <div ref={generationHost} className="artwork-generation-host flex min-h-0 min-w-0 flex-1 flex-col" /> : <div className="flex min-h-0 flex-1 flex-col">{kind.panel(actions, onClose)}</div>}
          </>
        )}
      </aside>
    </div>
  );
}
